import { useEffect, useRef } from "react";

import { buildInstructions, detectPlatform } from "./instructions";
import { sendMessageSentry } from "./sentry";
// El hook ya no renderiza UI por defecto; se exige templateAlert
import { safeReload } from "./utils";
import {
  AlertPlacement,
  RemoteVersionPayload,
  TemplateAlertEvent,
  UpdateFailureContext,
  UseReactCheckerVersionOptions
} from "./types";

const ATTEMPT_STORAGE_KEY = "__react_checker_version_attempt__";
const MANUAL_RETRY_DELAY_MS = 60 * 1000;

type StoredAttempt = {
  targetVersion: string;
  attemptedAt: string;
  userAgent: string;
  localVersionBefore?: string;
  projectName?: string;
};

const DEFAULT_PLACEMENT: Required<AlertPlacement> = {
  vertical: "bottom",
  horizontal: "left"
};

export function useReactCheckerVersion(options: UseReactCheckerVersionOptions) {
  const {
    nameProject,
    localProjectVersion,
    reevaluatingTime,
    waitTimeAfterDoLater,
    versionUrl = "/version.json",
    alertPlacement,
    refecthOnFocus,
    refetchOnFocus: explicitRefetchOnFocus,
    refetchOnReconnect: explicitRefetchOnReconnect,
    handleCatchErrorToUpdate,
    templateAlert,
    breakingAutoReloadDelayMs,
    enabled = true
  } = options;

  const placement: Required<AlertPlacement> = {
    vertical: alertPlacement?.vertical ?? DEFAULT_PLACEMENT.vertical,
    horizontal: alertPlacement?.horizontal ?? DEFAULT_PLACEMENT.horizontal
  };

  const shouldRefetchOnFocus = explicitRefetchOnFocus ?? refecthOnFocus ?? true;
  const shouldRefetchOnReconnect = explicitRefetchOnReconnect ?? true;

  const promptCleanupRef = useRef<(() => void) | null>(null);
  const reloadTimerRef = useRef<number | null>(null);
  const snoozeTimerRef = useRef<number | null>(null);
  const snoozeUntilRef = useRef<number | null>(null);
  const pollingTimerRef = useRef<number | null>(null);
  const isPromptVisibleRef = useRef(false);
  const lastNotifiedVersionRef = useRef<string | null>(null);
  const pendingAttemptRef = useRef<StoredAttempt | null>(null);
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!enabled) return;

    isMountedRef.current = true;

    const removeExistingAlertsDom = () => {
      try {
        const containers = document.querySelectorAll(
          '[data-react-checker-version-container]'
        );
        containers.forEach(el => {
          try {
            el.remove();
          } catch {}
        });
      } catch {}
      try {
        const overlays = document.querySelectorAll('[data-react-checker-version]');
        overlays.forEach(el => {
          try {
            el.remove();
          } catch {}
        });
      } catch {}
    };

    const clearPrompt = () => {
      if (promptCleanupRef.current) {
        promptCleanupRef.current();
        promptCleanupRef.current = null;
      }
      // Además, forzamos limpieza del DOM por si el template no devolvió cleanup
      removeExistingAlertsDom();
      isPromptVisibleRef.current = false;
    };

    const clearReloadTimer = () => {
      if (reloadTimerRef.current) {
        window.clearTimeout(reloadTimerRef.current);
        reloadTimerRef.current = null;
      }
    };

    const clearSnoozeTimer = () => {
      if (snoozeTimerRef.current) {
        window.clearTimeout(snoozeTimerRef.current);
        snoozeTimerRef.current = null;
      }
      snoozeUntilRef.current = null;
    };

    const clearPollingTimer = () => {
      if (pollingTimerRef.current) {
        window.clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };

    const closeAll = () => {
      clearPrompt();
      clearReloadTimer();
      clearSnoozeTimer();
    };

    const writeAttempt = (targetVersion: string) => {
      try {
        const payload = {
          targetVersion,
          attemptedAt: new Date().toISOString(),
          userAgent: navigator.userAgent,
          localVersionBefore: localProjectVersion,
          projectName: nameProject
        };
        sessionStorage.setItem(ATTEMPT_STORAGE_KEY, JSON.stringify(payload));
      } catch (error) {
        console.warn("useReactCheckerVersion: no se pudo guardar el intento", error);
      }
    };

    const readAttempt = () => {
      try {
        const raw = sessionStorage.getItem(ATTEMPT_STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as {
          targetVersion: string;
          attemptedAt: string;
          userAgent: string;
          localVersionBefore?: string;
          projectName?: string;
        };
      } catch (error) {
        console.warn("useReactCheckerVersion: no se pudo leer el intento", error);
        return null;
      }
    };

    const clearAttempt = () => {
      try {
        sessionStorage.removeItem(ATTEMPT_STORAGE_KEY);
      } catch (error) {
        console.warn("useReactCheckerVersion: no se pudo limpiar el intento", error);
      }
    };

    const logFailure = (ctx: UpdateFailureContext) => {
      sendMessageSentry({
        messageError: "version-update-failed",
        level: "warning",
        extra: ctx as unknown as Record<string, unknown>
      });

      try {
        handleCatchErrorToUpdate?.(ctx);
      } catch (error) {
        console.warn(
          "useReactCheckerVersion: handleCatchErrorToUpdate lanzó un error",
          error
        );
      }
    };

    const closePromptAndCleanup = () => {
      clearPrompt();
      clearReloadTimer();
      clearSnoozeTimer();
      isPromptVisibleRef.current = false;
    };

    const scheduleSnooze = () => {
      clearSnoozeTimer();
      if (waitTimeAfterDoLater <= 0) {
        snoozeUntilRef.current = null;
        void fetchRemote("snooze");
        return;
      }
      snoozeUntilRef.current = Date.now() + waitTimeAfterDoLater;
      snoozeTimerRef.current = window.setTimeout(() => {
        snoozeTimerRef.current = null;
        snoozeUntilRef.current = null;
        void fetchRemote("snooze");
      }, waitTimeAfterDoLater);
    };

    const showPrompt = (
      mode: "update" | "manual",
      remoteVersion: string,
      breaking: boolean,
      instructions = buildInstructions(detectPlatform(), nameProject)
    ) => {
      if (!isMountedRef.current) return;

      const onDismiss = () => {
        if (mode === "manual") {
          closePromptAndCleanup();
        } else {
          clearReloadTimer();
          closePromptAndCleanup();
          scheduleSnooze();
        }
      };

      const onReload = () => {
        clearSnoozeTimer();
        clearReloadTimer();
        closePromptAndCleanup();
        writeAttempt(remoteVersion);
        safeReload();
      };

      const templateParams: TemplateAlertEvent = {
        onDismiss,
        onReload,
        methodToForceUpdate: instructions.steps.join(" \u2022 "),
        methopToForceUpdate: instructions.steps.join(" \u2022 "),
        payload: {
          mode,
          remoteVersion,
          localVersion: localProjectVersion,
          breaking,
          autoReloadInMs: breakingAutoReloadDelayMs ?? MANUAL_RETRY_DELAY_MS,
          instructions,
          projectName: nameProject
        }
      };

      clearPrompt();
      isPromptVisibleRef.current = true;

      const result = templateAlert(templateParams) as unknown;
      // Si el template devuelve una función, la usamos como cleanup
      if (typeof result === "function") {
        promptCleanupRef.current = result as () => void;
      } else if (result && typeof (result as Promise<unknown>).then === "function") {
        void (result as Promise<unknown>).catch(error => {
          console.warn("useReactCheckerVersion: templateAlert rechazó", error);
        });
        promptCleanupRef.current = null;
      } else {
        promptCleanupRef.current = null;
      }
      return;
    };

    const inspectAttempt = () => {
      const attempt = readAttempt();
      if (!attempt) {
        pendingAttemptRef.current = null;
        return;
      }

      if (attempt.targetVersion === localProjectVersion) {
        clearAttempt();
        pendingAttemptRef.current = null;
        return;
      }

      pendingAttemptRef.current = attempt;
    };

    const fetchRemote = async (_reason: string) => {
      // Si estamos en período de snooze, evitamos consultas salvo la programada.
      if (
        snoozeUntilRef.current &&
        Date.now() < snoozeUntilRef.current &&
        _reason !== "snooze"
      ) {
        return;
      }
      try {
        const url = buildVersionUrl(versionUrl);
        const response = await fetch(url, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
            Expires: "0"
          }
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = (await response.json()) as RemoteVersionPayload;

        const remoteVersion = payload.version ?? "";
        const breaking = Boolean(payload.breaking);

        if (!remoteVersion) return;
        if (remoteVersion === localProjectVersion) {
          lastNotifiedVersionRef.current = null;
          clearPrompt();
          clearAttempt();
          pendingAttemptRef.current = null;
          clearReloadTimer();
          clearSnoozeTimer();
          return;
        }

        const pendingAttempt = pendingAttemptRef.current;
        if (pendingAttempt && pendingAttempt.targetVersion === remoteVersion) {
          const instructions = buildInstructions(
            detectPlatform(),
            pendingAttempt.projectName ?? nameProject
          );
          const ctx: UpdateFailureContext = {
            userAgent: pendingAttempt.userAgent,
            platform: instructions.platform,
            localVersion: localProjectVersion,
            targetVersion: pendingAttempt.targetVersion,
            attemptedAt: pendingAttempt.attemptedAt,
            detectedAt: new Date().toISOString(),
            projectVersionAfterReload: localProjectVersion,
            currentUrl: window.location.href,
            projectName: pendingAttempt.projectName ?? nameProject
          };

          logFailure(ctx);
          showPrompt("manual", remoteVersion, breaking, instructions);
          clearAttempt();
          pendingAttemptRef.current = null;
          lastNotifiedVersionRef.current = remoteVersion;
          return;
        }

        pendingAttemptRef.current = null;

        if (
          lastNotifiedVersionRef.current === remoteVersion &&
          isPromptVisibleRef.current
        ) {
          return;
        }

        lastNotifiedVersionRef.current = remoteVersion;

        if (breaking) {
          clearReloadTimer();
          const delay = breakingAutoReloadDelayMs ?? MANUAL_RETRY_DELAY_MS;
          reloadTimerRef.current = window.setTimeout(() => {
            reloadTimerRef.current = null;
            safeReload();
          }, delay);
        }

        const instructions = buildInstructions(detectPlatform(), nameProject);
        showPrompt("update", remoteVersion, breaking, instructions);
      } catch (error) {
        console.warn(
          "useReactCheckerVersion: error obteniendo version remota",
          error
        );
      }
    };

    inspectAttempt();
    void fetchRemote("initial");

    if (reevaluatingTime > 0) {
      pollingTimerRef.current = window.setInterval(() => {
        void fetchRemote("polling");
      }, reevaluatingTime);
    }

    const focusHandler = () => {
      if (shouldRefetchOnFocus) {
        void fetchRemote("focus");
      }
    };

    const onlineHandler = () => {
      if (shouldRefetchOnReconnect) {
        void fetchRemote("reconnect");
      }
    };

    const visibilityHandler = () => {
      if (document.visibilityState === "visible" && shouldRefetchOnFocus) {
        void fetchRemote("visibility");
      }
    };

    window.addEventListener("focus", focusHandler);
    window.addEventListener("online", onlineHandler);
    document.addEventListener("visibilitychange", visibilityHandler);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener("focus", focusHandler);
      window.removeEventListener("online", onlineHandler);
      document.removeEventListener("visibilitychange", visibilityHandler);
      clearPollingTimer();
      closeAll();
    };
  }, [
    nameProject,
    localProjectVersion,
    versionUrl,
    waitTimeAfterDoLater,
    templateAlert,
    handleCatchErrorToUpdate,
    placement.vertical,
    placement.horizontal,
    refecthOnFocus,
    explicitRefetchOnFocus,
    explicitRefetchOnReconnect,
    shouldRefetchOnFocus,
    shouldRefetchOnReconnect,
    reevaluatingTime,
    breakingAutoReloadDelayMs,
    enabled
  ]);
}

function buildVersionUrl(versionUrl: string): string {
  if (typeof window === "undefined") return versionUrl;
  try {
    const url = new URL(versionUrl, window.location.origin);
    url.searchParams.set("t", Date.now().toString());
    return url.toString();
  } catch (error) {
    console.warn("useReactCheckerVersion: URL inválida, se usará literal", error);
    const separator = versionUrl.includes("?") ? "&" : "?";
    return `${versionUrl}${separator}t=${Date.now().toString()}`;
  }
}

export default useReactCheckerVersion;
